<template>
  <div class="py-4">
    <SmButton
      outline
      size="smaller"
      class="group w-full justify-start"
      @click="openAliasDialog">
      {{ props.license.email }}
    </SmButton>

    <SmDialog
      v-model:visibility="aliasDialogVisible"
      size="medium"
      :title="i18n.t('alias.connectAliasTitle')">
      <div class="flex flex-col gap-3">
        <div class="mb-1 flex w-full items-center gap-3 rounded border p-4">
          <ApplicationIcon
            :application-name="props.applicationName"
            :application-type="props.applicationType"
            class="shrink-0"
            :size="48" />

          <div class="flex flex-col">
            <TextMultiline
              :text="props.applicationDisplayName || props.applicationName"
              class="font-bold" />
            <TextMultiline
              :text="`${getAccountIdentifierLabel()}: ${props.license.email}`" />
            <TextMultiline
              v-if="props.license.name"
              :text="`${i18n.t('name')}: ${props.license.name}`"
              class="text-gray-400" />
          </div>
        </div>

        <p class="mb-2 text-sm text-contrast-muted">
          {{ i18n.t('alias.assignAliasHint') }}
        </p>

        <SmSelect
          v-model:selection="selection"
          :options="lazyOptions"
          :item-size="66"
          label=""
          :placeholder="
            selectedAlias ? '' : i18n.t('alias.searchEmailPlaceholder')
          "
          :placeholder-icon="Search"
          searchable
          trigger-style="default"
          class="mb-2"
          @change="handleAliasSelectionChange">
          <template #item="{ item }">
            <div class="flex flex-col leading-5">
              <span>{{ item.label }}</span>
              <span class="text-sm text-contrast-muted">{{
                item.description
              }}</span>
            </div>
          </template>
        </SmSelect>
      </div>

      <template #footer>
        <div class="flex gap-2">
          <SmButton outline @click="closeAliasDialog">
            {{ i18n.t('general.cancel') }}
          </SmButton>
          <SmButton
            type="primary"
            :disabled="!selectedAlias || isConnecting"
            @click="connectAlias">
            {{ i18n.t('general.connect') }}
          </SmButton>
        </div>
      </template>
    </SmDialog>
  </div>
</template>

<script lang="ts" setup>
  import { Search } from 'lucide-vue-next'
  import { useI18n } from 'vue-i18n'

  import { LicenceOut, SoftwareTypes, UserAliasesService } from '@/client'
  import ApplicationIcon from '@/components/applications/ApplicationIcon.vue'

  import { editToast, sendToast } from '@/components/sm/SmNotification'
  import { Option } from '@/components/sm/SmSelect.vue'

  const i18n = useI18n()

  const props = defineProps({
    license: { type: Object as PropType<LicenceOut>, required: true },
    applicationName: { type: String, required: true },
    applicationDisplayName: { type: String, required: true },
    applicationType: {
      type: String as PropType<SoftwareTypes | 'SSO' | null>,
      default: null,
    },
    getOptions: {
      type: Function as PropType<() => Option<string>[]>,
      required: true,
    },
  })

  const emit = defineEmits(['licenseChanged'])

  const aliasDialogVisible = ref<boolean>(false)
  const selectedAlias = ref<Option<string>>()
  const selection = ref<Option<string>[]>([])
  const isConnecting = ref<boolean>(false)

  const lazyOptions = ref<Option<string>[]>([])
  let loaded = false

  function ensureOptions() {
    if (loaded) return
    loaded = true
    lazyOptions.value = props.getOptions()
  }

  function openAliasDialog() {
    ensureOptions()
    aliasDialogVisible.value = true
  }

  function closeAliasDialog() {
    aliasDialogVisible.value = false
    selectedAlias.value = undefined
    selection.value = []
  }

  function handleAliasSelectionChange(
    newSelection: Option<string>[] | undefined
  ) {
    selectedAlias.value = newSelection?.[0]
  }

  function isEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  function getAccountIdentifierLabel() {
    return isEmail(props.license.email) ? i18n.t('email') : i18n.t('username')
  }

  function connectAlias() {
    if (!selectedAlias.value || isConnecting.value) return
    isConnecting.value = true

    const toastId = sendToast(
      i18n.t('notification.loading'),
      undefined,
      'loading'
    )

    UserAliasesService.addAliasApiV1ManagementUsersEmailOrIdAliasPost({
      // Current email
      emailOrId: selectedAlias.value.value,
      requestBody: {
        // Username that should be added to the account
        user_name: props.license.email,
      },
    })
      .then(() => {
        emit('licenseChanged')
        closeAliasDialog()
        editToast(toastId, {
          title: i18n.t('alias.connectAliasSuccess'),
          message: '',
          type: 'success',
        })
      })
      .catch(() => {
        editToast(toastId, {
          title: i18n.t('notification.loadingError'),
          message: '',
          type: 'error',
        })
      })
      .finally(() => {
        isConnecting.value = false
      })
  }
</script>
